/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { NetworkManager } from '../../../core/network/NetworkManager';
import { CursorManager } from '../../../core/navigation';
import { VideoManager } from '../../../core/videoplayer';
import { DeviceFeatures } from '../../../core/device';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME } from '../../../config';
import { videoPlayerIsMediaButtonPressedAction } from '../../videoplayer/videoplayer.actions';
import {
    handleAdvance,
    handleFastForward,
    handleReplay,
    handleRewind,
    hideNotSupportedText,
    resetPlaybackParamsVideoplayer,
    switchToLiveTv,
    toggleIsPlaying,
} from '../../videoplayer/videoplayer.reducers';
import {
    setCurrentActiveIndexAction,
    updateTrickPlayBarLongKeyPressIntervalIdAction,
} from './infobanner.actions';
import {
    clearLongKeyPressInterval,
    hideInfoBanners,
    hideTrickPlayMenu,
    showFullInfo,
    updateStandardInfoBannerTimer,
    updateTrickPlayMenuTimer,
} from './infobanner.reducers';
import { MediaButtonTypes } from './mediabuttontypes';
import { PlaybackState, TrickPlayType } from './PlaybackTypes';
import { hideVideoDebugOverlay } from '../../videoplayer/videodebugoverlay/videodebugoverlay.reducers';
import { InfoBannerUtils } from '../../../views/VideoPlayerUI/FullInfoBanner/EventDescription/InfoBannerUtils';
import { prepareVodResumeStartOverOverlayFromLiveTv } from '../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { SocuDataModelImpl } from '../../../core/socu/impls';

export const onKeyLeftTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    dispatch(updateStandardInfoBannerTimer());
    dispatch(updateTrickPlayMenuTimer());
    const { infoBanner } = getState();
    const { currentActiveIndex, activePositionsAvailable } = infoBanner;
    const positionsSize = activePositionsAvailable.size;
    if (positionsSize && currentActiveIndex > 0) {
        let currentIndex = currentActiveIndex;
        let index = currentActiveIndex - 1;
        if (!activePositionsAvailable.get(index).isActive) {
            for (index; index >= 0; index -= 1) {
                if (activePositionsAvailable.get(index).isActive) {
                    currentIndex = index;
                    break;
                }
            }
        } else {
            currentIndex = index;
        }
        dispatch(setCurrentActiveIndexAction(currentIndex));
    }

    dispatch(hideNotSupportedText());
};

export const onKeyLongLeftTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { trickPlayBarLongKeyPressIntervalId } = getState().infoBanner;
    if (!trickPlayBarLongKeyPressIntervalId) {
        const intervalId = setInterval(() => {
            dispatch(onKeyLeftTrickplayMenu());
        }, TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME);
        dispatch(updateTrickPlayBarLongKeyPressIntervalIdAction(intervalId));
    }
};

export const clearLongPressIntervalTrickplayMenu = () => (dispatch: Function) => {
    dispatch(clearLongKeyPressInterval());
};

export const onKeyRightTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    dispatch(updateStandardInfoBannerTimer());
    dispatch(updateTrickPlayMenuTimer());
    const { infoBanner } = getState();
    const { currentActiveIndex, activePositionsAvailable } = infoBanner;
    const positionsSize = activePositionsAvailable.size;
    if (positionsSize && currentActiveIndex < positionsSize - 1) {
        let index = currentActiveIndex + 1;
        if (!activePositionsAvailable.get(index).isActive) {
            for (index; index < activePositionsAvailable.size - 1; index += 1) {
                if (activePositionsAvailable.get(index).isActive) {
                    break;
                }
            }
        }
        dispatch(setCurrentActiveIndexAction(index));
    }

    dispatch(hideNotSupportedText());
};

export const onKeyLongRightTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { trickPlayBarLongKeyPressIntervalId } = getState().infoBanner;
    if (!trickPlayBarLongKeyPressIntervalId) {
        const intervalId = setInterval(() => {
            dispatch(onKeyRightTrickplayMenu());
        }, TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME);
        dispatch(updateTrickPlayBarLongKeyPressIntervalIdAction(intervalId));
    }
};

export const onKeyOkTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { infoBanner, videoPlayer } = getState();
    const { currentActiveIndex, activePositionsAvailable } = infoBanner;
    const { airingOffers, offerPreview } = VideoManager.getInstance().getMetadata();
    const { playbackState } = videoPlayer;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    const isCursorOn = CursorManager.isCursorAppeared();
    let activePosition = activePositionsAvailable.get(currentActiveIndex)?.type;
    const dataFormatter = new InfoBannerUtils(airingOffers, offerPreview);
    const isAvailableInSocu = dataFormatter.getIsAvailableInSocu();
    const isSocuEnabled = DeviceFeatures.isSocuEnabled();

    if (isCursorOn) {
        const { value } = getState().inputFactory;
        const valueInt = Number(value);
        if (valueInt >= 0) {
            activePosition = valueInt;
            dispatch(setCurrentActiveIndexAction(activePosition));
        }
    }
    dispatch(updateTrickPlayMenuTimer());
    dispatch(updateStandardInfoBannerTimer());

    switch (activePosition) {
        case MediaButtonTypes.START_FROM_BUFFER_BUTTON_INDEX:
            // socu is available in preview data but socu content is not playing.
            if (isSocuEnabled && isAvailableInSocu && !SocuDataModelImpl.getInstance().isSocu()) {
                dispatch(hideInfoBanners());
                dispatch(prepareVodResumeStartOverOverlayFromLiveTv());
            } else {
                videoPlayerIns.startFromBuffer();
                if (
                    !videoPlayerIns.isTrickplayEnable(playbackState) &&
                    playbackState !== PlaybackState.PLAY
                )
                    dispatch(toggleIsPlaying());
            }
            break;
        case MediaButtonTypes.REPLAY_BUTTON_INDEX:
            dispatch(handleReplay());
            break;
        case MediaButtonTypes.REWIND_BUTTON_INDEX:
            dispatch(handleRewind());
            break;
        case MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX:
            if (videoPlayerIns.isPlaybackStarted() && !NetworkManager.isNetworkDisconnected()) {
                if (videoPlayerIns.trickModeRestrictions()?.allowPause) {
                    dispatch(toggleIsPlaying());
                } else if (videoPlayerIns.isTrickplayEnable(playbackState)) {
                    dispatch(toggleIsPlaying());
                } else {
                    dispatch(
                        videoPlayerIsMediaButtonPressedAction({
                            type: TrickPlayType.PAUSE,
                            isMediaButtonPressed: true,
                        }),
                    );
                }
            }
            break;
        case MediaButtonTypes.FORWARD_BUTTON_INDEX:
            dispatch(handleFastForward());
            break;
        case MediaButtonTypes.ADVANCE_BUTTON_INDEX:
            dispatch(handleAdvance());
            break;
        case MediaButtonTypes.GO_TO_LIVETV_BUTTON_INDEX:
            // TODO: Need to check if socu content is playing then handle Go to End
            videoPlayerIns.goToLive();
            if (
                !videoPlayerIns.isTrickplayEnable(playbackState) &&
                playbackState !== PlaybackState.PLAY
            )
                dispatch(toggleIsPlaying());
            break;
        case MediaButtonTypes.LIVE_BUTTON_INDEX:
            dispatch(resetPlaybackParamsVideoplayer());
            dispatch(hideInfoBanners());
            dispatch(switchToLiveTv());
            break;
        case MediaButtonTypes.INFO_BUTTON_INDEX:
            if (videoPlayerIns.isTrickplayEnable(playbackState)) {
                dispatch(toggleIsPlaying());
            }
            dispatch(hideTrickPlayMenu());
            dispatch(showFullInfo());
            break;
        default:
        // callback to default case
    }
};

export const onKeyBackTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { isVideoDebugOverlayActive } = getState().videoDebugOverlay;
    if (isVideoDebugOverlayActive) {
        dispatch(hideVideoDebugOverlay());
    }
    const { playbackState } = getState().videoPlayer;
    if (
        VideoManager.getInstance().getVideoPlayerModel().isTrickplayEnable(playbackState) &&
        !NetworkManager.isNetworkDisconnected()
    )
        dispatch(toggleIsPlaying());
    dispatch(hideTrickPlayMenu());
};

export const onKeyInfoTrickplayMenu = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    if (VideoManager.getInstance().getVideoPlayerModel().isTrickplayEnable(playbackState))
        dispatch(toggleIsPlaying());
    dispatch(hideTrickPlayMenu());
    dispatch(showFullInfo());
};

export const onKeyChannelDownTrickplayMenu = () => (dispatch: Function) => {
    dispatch(updateTrickPlayMenuTimer());
    dispatch(updateStandardInfoBannerTimer());
};

export const onKeyChannelUpTrickplayMenu = () => (dispatch: Function) => {
    dispatch(updateTrickPlayMenuTimer());
    dispatch(updateStandardInfoBannerTimer());
};
